const CATEGORY_DAYS = Object.freeze([
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday"
]);

const createEmptyCategoryState = () =>
    CATEGORY_DAYS.reduce((accumulator, day) => {
        accumulator[day] = {};
        return accumulator;
    }, {});

const cloneCategoryDay = (dayData = {}) => {
    const cloned = {};
    Object.keys(dayData).forEach((category) => {
        const selections = Array.isArray(dayData[category])
            ? [...dayData[category]]
            : [];
        cloned[category] = selections;
    });
    return cloned;
};

const cloneCategoryState = (state = {}) => {
    const cloned = createEmptyCategoryState();
    CATEGORY_DAYS.forEach((day) => {
        cloned[day] = cloneCategoryDay(state[day]);
    });
    return cloned;
};

const normalizeCategoryState = (state = {}) => {
    const normalized = createEmptyCategoryState();
    CATEGORY_DAYS.forEach((day) => {
        const source = state[day] || {};
        const normalizedDay = {};
        Object.keys(source).forEach((category) => {
            const selections = source[category];
            if (Array.isArray(selections)) {
                normalizedDay[category] = [...selections];
            }
        });
        normalized[day] = normalizedDay;
    });
    return normalized;
};

const serializeDay = (dayData = {}) => {
    const ordered = {};
    Object.keys(dayData)
        .sort()
        .forEach((category) => {
            const list = Array.isArray(dayData[category])
                ? [...dayData[category]].sort()
                : [];
            ordered[category] = list;
        });
    return JSON.stringify(ordered);
};

const areDaysUniform = (state = {}) => {
    if (CATEGORY_DAYS.length === 0) {
        return true;
    }
    const baseline = serializeDay(state[CATEGORY_DAYS[0]]);
    return CATEGORY_DAYS.every((day) => serializeDay(state[day]) === baseline);
};

const replicateStateFromDay = (state = {}, baseDay = CATEGORY_DAYS[0]) => {
    const base = cloneCategoryDay(state[baseDay]);
    const replicated = createEmptyCategoryState();
    CATEGORY_DAYS.forEach((day) => {
        replicated[day] = cloneCategoryDay(base);
    });
    return replicated;
};

const formatDayLabel = (day) => `${day.charAt(0).toUpperCase()}${day.slice(1)}`;

/**
 * Returns an object describing which categories are unique to specific days.
 * For each day, lists subcategories that are present on that day but NOT on all days.
 * Shape: { hasDifferences: boolean, dayDiffs: [{ day, category, subcategories }] }
 */
const getDayDifferences = (state = {}) => {
    const dayDiffs = [];

    // Build a set of all (category, subcategory) pairs across all days
    const allPairs = new Map();
    CATEGORY_DAYS.forEach((day) => {
        const dayData = state[day] || {};
        Object.keys(dayData).forEach((category) => {
            (dayData[category] || []).forEach((sub) => {
                const key = `${category}::${sub}`;
                if (!allPairs.has(key)) allPairs.set(key, new Set());
                allPairs.get(key).add(day);
            });
        });
    });

    // Find pairs that are NOT on all 7 days (i.e. non-uniform)
    const nonUniform = [];
    allPairs.forEach((days, key) => {
        if (days.size < CATEGORY_DAYS.length) {
            const [category, sub] = key.split("::");
            nonUniform.push({ category, sub, days });
        }
    });

    // Group by day
    const byDay = {};
    CATEGORY_DAYS.forEach((day) => { byDay[day] = {}; });
    nonUniform.forEach(({ category, sub, days }) => {
        days.forEach((day) => {
            if (!byDay[day][category]) byDay[day][category] = [];
            byDay[day][category].push(sub);
        });
    });

    CATEGORY_DAYS.forEach((day) => {
        const categories = byDay[day];
        Object.keys(categories).sort().forEach((category) => {
            dayDiffs.push({ day, category, subcategories: categories[category].sort() });
        });
    });

    return { hasDifferences: dayDiffs.length > 0, dayDiffs };
};

/**
 * Returns a Set of "categoryName::subcategory" keys that are present
 * on every single day — i.e. they are uniform across the week.
 */
const getUniformSubcategories = (state = {}) => {
    const uniform = new Set();
    const firstDay = state[CATEGORY_DAYS[0]] || {};

    Object.keys(firstDay).forEach((category) => {
        (firstDay[category] || []).forEach((sub) => {
            uniform.add(`${category}::${sub}`);
        });
    });

    for (let i = 1; i < CATEGORY_DAYS.length; i++) {
        const dayData = state[CATEGORY_DAYS[i]] || {};
        uniform.forEach((key) => {
            const [category, sub] = key.split("::");
            const dayList = dayData[category] || [];
            if (!dayList.includes(sub)) {
                uniform.delete(key);
            }
        });
    }

    return uniform;
};

export {
    CATEGORY_DAYS,
    createEmptyCategoryState,
    cloneCategoryState,
    normalizeCategoryState,
    areDaysUniform,
    replicateStateFromDay,
    formatDayLabel,
    getDayDifferences,
    getUniformSubcategories
};
